import React, {
  useEffect,
  useRef,
  ReactElement,
  useState,
  useCallback,
} from "react";
import type { GetStaticProps, GetStaticPaths } from "next";
import styled from "styled-components";
import NextHeadSeo from "next-head-seo";

import debounce from "lodash/debounce";
import screenfull from "~/utils/SSRScreenFull";
import { primaryInput } from "detect-it";

import LayoutIntroSlideshow from "~/components/layouts/LayoutIntroSlideshow";
import { Header } from "~/components/app/Header";
import PageMargins from "~/components/styled/PageMargins";
import {
  restApiGetPostBySlugOrFallbackId,
  restApiESGetSettings,
  restApiESQuery,
} from "~/utils/restApi";
import ContentWithSidebar from "~/components/styled/ContentWithSidebar";
import { FlexibleContent } from "~/components/flexibleContent/FlexibleContent";
import Sidebar from "~/components/sidebars/Sidebar";
import { SidebarShare } from "~/components/sidebars/SidebarShare";
import { useConfigContext } from "~/providers/ConfigContextProvider";
import { Footer } from "~/components/app/Footer";
import { Heading } from "~/components/ui/Heading";
import { Badge } from "~/components/ui/Badge";
import { formatDate } from "~/utils";
import SubMenuTwoCol from "~/components/styled/SubMenuTwoCol";
import DisplayAbove from "~/components/styled/DisplayAbove";
import SideBarBlock from "~/components/sidebars/SideBarBlock";
import SafeHtmlDiv from "~/components/ui/SafeHtmlDiv";
import dynamic from "next/dynamic";

import { appConfig } from "~/config";
import ExhibitionSlideShow from "~/components/content/ExhibitionSlideShow";
import { ApiImage } from "~/components/ui/ApiImage";
import Button from "~/components/styled/Button";
import { SvgBackground } from "~/components/ui/SvgBackground";
import SvgFullscreenExit from "~/components/svgs/SvgFullscreenExit";
import SvgFullscreen from "~/components/svgs/SvgFullscreen";
import useIsMounted from "~/hooks/useIsMounted";
import { useHeaderContext } from "~/providers/HeaderContextProvider";
import { themeImgSizes } from "~/theme";
import { BookTicket } from "~/components/ui/BookTicket";
import { Map } from "~/components/ui/Map";
import { useMenuContext } from "~/providers/MenuContextProvider";

import { SidebarBuyButtonWrapper } from "~/components/styled/SidebarBuyButtonWrapper";
import SafeHtmlSpan from "~/components/ui/SafeHtmlSpan";
import { useCssVarsContext } from "~/providers/CssVarsContextProvider";
import { RelatedContent } from "~/components/content/RelatedContent";
import { RelatedProducts } from "~/components/content/RelatedProducts";

const Documents = dynamic(() => import("~/components/ui/Documents"));

const FonaBannerGlobalStyle = dynamic(
  () => import("~/components/ui/FonaBannerGlobalStyle")
);

const VerticalSlider = dynamic(() => import("~/components/ui/VerticalSlider"));
const ListItemVideoOrAudio = dynamic(
  () => import("~/components/content/ListItemVideoOrAudio")
);

const SLIDES_VIRTUAL_MIN = 9;

const uA =
  typeof navigator !== "undefined" ? navigator.userAgent.toLowerCase() : "";
const isChrome = uA.indexOf("chrome") !== -1;
const isSafari =
  (uA.indexOf("safari") !== -1 ||
    uA.indexOf("applewebkit") !== -1 ||
    uA.indexOf("ipad") !== -1 ||
    uA.indexOf("iphone") !== -1) &&
  !isChrome;

const imgSizes = {
  tablet: {
    landscape: "95vw",
    portrait: "45vw",
  },
  mobileLandscape: {
    landscape: "100vw",
    portrait: "70vw",
  },
  base: {
    landscape: "120vw",
    portrait: "70vw",
  },
};

const parsedMatterportEmbed = (embed: string) => {
  const regExp = new RegExp(
    "<iframe[^>]*src=[\"|']([^'\"]+)[\"|'][^>]*>",
    "ig"
  );

  const matches = regExp.exec(embed.trim());

  if (matches && matches?.length > 1) {
    return `<iframe width="100%" height="100%" frameborder="0" src="${matches[1]}" loading="lazy" allowfullscreen allow="xr-spatial-tracking"></iframe>`;
  }
  return embed;
};

const ExhibitionHeader = styled.div`
  width: 100%;
  height: calc(100vh - var(--lbh, 0px));
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const ExhibitionContentShadow = styled.div``;

const ExhibitionContent = styled.div<{ isMenuOpen?: boolean }>`
  width: ${({ isMenuOpen }) =>
    isMenuOpen ? "calc(100vw - var(--sbw, 0))" : "100%"};
  position: fixed;
  top: 0px;
  z-index: 1;
  transform: translateZ(0);
`;

const SlideShow = styled.div<{
  isMenuOpen?: boolean;
  isFullscreen: boolean;
  isIOS: boolean;
}>`
  width: ${({ isMenuOpen }) =>
    isMenuOpen ? "calc(100vw - var(--sbw, 0))" : "100%"};
  height: ${({ isFullscreen, isIOS }) =>
    isFullscreen ? (isIOS ? "calc(100%)" : "100vh") : "100vh"};
  overflow: hidden;
  position: ${({ isFullscreen }) => (isFullscreen ? "fixed" : "absolute")};
  top: 0;
  left: 0;
  z-index: 3;
  background-color: ${({ isFullscreen }) => (isFullscreen ? "#fff" : "#000")};
`;

const Blur = styled.div<{ isMenuOpen: boolean; isFullscreen: boolean }>`
  width: ${({ isMenuOpen }) =>
    isMenuOpen ? "calc(100vw - var(--sbw, 0))" : "100%"};
  height: 100vh;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 4;
  background-image: linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.5));
  transition: opacity 0.5s;
  pointer-events: none;

  ${({ isFullscreen }) =>
    isFullscreen
      ? `
    opacity: 0;
  `
      : ``};
`;

const Overlay = styled.div<{ isFullscreen: boolean; isMenuOpen?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 5;
  width: ${({ isMenuOpen }) =>
    isMenuOpen ? "calc(100vw - var(--sbw, 0))" : "100%"};
  height: 100vh;
  min-height: ${({ isFullscreen }) =>
    isFullscreen ? "100vh !important" : "auto"};
  overflow: hidden;
  transform: translateZ(0);
  pointer-events: none;
`;

const OverlayContent = styled.div`
  position: absolute;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  color: #fff;
  transition: opacity 0.5s;
  width: 100%;
  padding-bottom: var(--lbh, 0px);
`;

const TitleGrid = styled.div<{ isFullscreen: boolean }>`
  display: grid;
  align-items: end;
  ${(props) =>
    props.theme.apply(["base", "mobile", "tablet"], (breakpoint: string) => {
      return `
          grid-template-columns: 1fr ${props.theme.spacePx(breakpoint, 6)};
          grid-gap: ${props.theme.spacePx(breakpoint, 6)};          
        `;
    })}

  ${(props) =>
    props.theme.apply(
      ["tabletLandscape", "desktop", "screen"],
      (breakpoint: string) => {
        return `
          grid-template-columns: 1fr ${props.theme.spacePx(breakpoint, 3)};
          grid-gap: ${props.theme.spacePx(breakpoint, 6)};          
        `;
      }
    )}
  transition: opacity 0.5s;
  ${({ isFullscreen }) =>
    isFullscreen
      ? `
    opacity: 0;
  `
      : ``};
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
          padding-top: ${props.theme.spacePx(breakpoint, 9)};          
        `;
    })}
`;

const Badges = styled.div`
  grid-area: badges;
  display: flex;

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
          column-gap: ${props.theme.spacePx(breakpoint, 9)};          
        `;
    })}
`;

const Info = styled.div`
  display: grid;
  grid-template-areas:
    "badges"
    "text";

  ${({ theme }) => theme.breakpoints.tablet} {
    grid-template-columns: auto 1fr;
    grid-template-areas: "text badges";
    width: auto;
  }

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
          column-gap: ${props.theme.spacePx(breakpoint, 8)};
          padding-bottom: ${(
            props.theme.space(breakpoint, 7) - props.theme.space(breakpoint, 9)
          ).toFixed(2)}px;          
        `;
    })}
`;

const MapDiv = styled.div`
  margin: auto;
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        padding-top: ${props.theme.spacePx(breakpoint, 8)};          
        
    `;
    })}
`;

const SlideshowButtonContainer = styled.div<{
  isFullscreen: boolean;
  isIOS: boolean;
}>`
  display: flex;
  position: absolute;
  z-index: 3;

  ${(props) =>
    props.isFullscreen
      ? `
          
        top: ${props.isIOS ? "calc(100vh - var(--lbh, 0px))" : "100vh"};  
        ${props.theme.apply("default", (breakpoint: string) => {
          return `
            flex-direction: row;
            right: ${props.theme.spacePx(breakpoint, 7)};
            transform: translateY(calc(-100% - ${props.theme.spacePx(
              breakpoint,
              7
            )}));

            
            column-gap: ${props.theme.spacePx(breakpoint, 8)}; 
            row-gap: ${props.theme.spacePx(breakpoint, 8)};          
        `;
        })}`
      : `
        justify-content: flex-end;
      
        top: 100vh;  
        transform: translateY(-100%);

        ${props.theme.apply(["base", "mobile"], (breakpoint: string) => {
          return `
                flex-direction: column;
                
                padding-bottom: calc(${props.theme.spacePx(
                  breakpoint,
                  6
                )} + var(--lbh, 0px));
                right: ${props.theme.marginPx(breakpoint)};
                row-gap: ${props.theme.spacePx(breakpoint, 7)};
                column-gap: ${props.theme.spacePx(breakpoint, 7)};         
              `;
        })}

        ${props.theme.apply(["tablet"], (breakpoint: string) => {
          return `
                flex-direction: column;
                
                padding-bottom: calc(${props.theme.spacePx(
                  breakpoint,
                  6
                )} + var(--lbh, 0px));
                right: ${props.theme.marginPx(breakpoint)};
                row-gap: ${props.theme.spacePx(breakpoint, 8)};
                column-gap: ${props.theme.spacePx(breakpoint, 8)}; 
              `;
        })}

        ${props.theme.apply(
          ["tabletLandscape", "desktop", "screen"],
          (breakpoint: string) => {
            return `
              flex-direction: row;
              padding-bottom: calc(${props.theme.spacePx(
                breakpoint,
                6
              )} + var(--lbh, 0px));
              right: ${props.theme.marginPx(breakpoint)};
              column-gap: ${props.theme.spacePx(breakpoint, 8)};  
              `;
          }
        )}`}
`;

const SlideshowButton = styled(Button)<{
  rotateButton?: boolean;
  arrow?: boolean;
  isFullscreen: boolean;
}>`
  transform: ${({ rotateButton }) => (rotateButton ? "rotate(180deg)" : "")};

  transition: filter 0.5s;
  pointer-events: all;
  filter: ${({ isFullscreen }) =>
    isFullscreen ? "invert(0%)" : "invert(100%)"};

  &:disabled {
    opacity: 0.5;
    cursor: default;
    pointer-events: none;
    touch-action: none;
  }

  ${(props) =>
    props.theme.apply(["base", "mobile"], (breakpoint: string) => {
      return `
        width: ${props.theme.spacePx(breakpoint, 6)};
        height: ${(
          props.theme.space(breakpoint, 6) * (props.arrow ? 1 / 1.1 : 1)
        ).toFixed(2)}px;
       
        `;
    })}

  ${(props) =>
    props.theme.apply(["tablet"], (breakpoint: string) => {
      return `
        width: ${props.theme.spacePx(breakpoint, 7)};
        height: ${(
          props.theme.space(breakpoint, 7) * (props.arrow ? 1 / 1.1 : 1)
        ).toFixed(2)}px;
       
        `;
    })}

  ${(props) =>
    props.theme.apply(
      ["tabletLandscape", "desktop", "screen"],
      (breakpoint: string) => {
        return `
        width: ${(
          props.theme.space(breakpoint, 7) * (props.arrow ? 1.1 : 1)
        ).toFixed(2)}px;
        height: ${props.theme.spacePx(breakpoint, 7)};
       
        `;
      }
    )}
`;

const ExhibitionSlide = styled.div<{ isFullscreen: boolean }>`
  display: ${({ isFullscreen }) => (isFullscreen ? "grid" : "block")};
  width: 100%;
  height: 100%;

  ${({ isFullscreen }) =>
    !isFullscreen
      ? `
      & > div {
        height: 100%;
      }
    `
      : ``};

  ${(props) =>
    props.theme.apply(["base", "mobile"], (breakpoint: string) => {
      return `
        ${props.theme.textStyle(breakpoint, "caption")}; 

        grid-column-gap: ${props.theme.spacePx(breakpoint, 7)};
        grid-row-gap: ${props.theme.spacePx(breakpoint, 7)};

        @media (orientation: landscape) {
          grid-template-columns: calc(100% - ${props.theme.spacePx(
            breakpoint,
            2
          )}) ${(
        props.theme.space(breakpoint, 6) * 5 +
        props.theme.space(breakpoint, 8) * 2
      ).toFixed(2)}px;
          grid-template-rows: 100%;        
        }

        @media (orientation: portrait) {
          grid-template-columns: 100%;
          grid-template-rows: calc(100% - ${(
            props.theme.space(breakpoint, 2) + props.theme.space(breakpoint, 7)
          ).toFixed(0)}px) ${props.theme.space(breakpoint, 2).toFixed(0)}px;
        }   
      `;
    })}

  ${(props) =>
    props.theme.apply(["tablet"], (breakpoint: string) => {
      return `
        ${props.theme.textStyle(breakpoint, "caption")}; 
        grid-column-gap: ${props.theme.spacePx(breakpoint, 7)};
        grid-row-gap: ${props.theme.spacePx(breakpoint, 7)};

        @media (orientation: landscape) {
          grid-template-columns: calc(100% - ${(
            props.theme.space(breakpoint, 7) + 220
          ).toFixed(2)}px) 220px;
          grid-template-rows: 100%;        
        }

        @media (orientation: portrait) {
          grid-template-columns: 100%;
          grid-template-rows: calc(100% - ${(
            props.theme.space(breakpoint, 3) + props.theme.space(breakpoint, 7)
          ).toFixed(0)}px) ${props.theme.space(breakpoint, 3).toFixed(0)}px;
        }   
      `;
    })}

  ${(props) =>
    props.theme.apply(
      ["tabletLandscape", "desktop", "screen"],
      (breakpoint: string) => {
        return `
        ${props.theme.textStyle(breakpoint, "caption")}; 

        grid-column-gap: ${props.theme.spacePx(breakpoint, 7)};
        grid-row-gap: ${props.theme.spacePx(breakpoint, 7)};

        @media (orientation: landscape) {
          grid-template-columns: calc(100% - ${(
            props.theme.space(breakpoint, 2) + props.theme.space(breakpoint, 7)
          ).toFixed(2)}px) ${props.theme.spacePx(breakpoint, 2)};
          grid-template-rows: 100%;        
        }

        @media (orientation: portrait) {
          grid-template-columns: 100%;
          grid-template-rows: calc(100% - ${(
            props.theme.space(breakpoint, 3) + props.theme.space(breakpoint, 7)
          ).toFixed(0)}px) ${props.theme.space(breakpoint, 3).toFixed(0)}px;
        }   
      `;
      }
    )}
`;
const Caption = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  ${(props) =>
    props.theme.apply(["base", "mobile"], (breakpoint: string) => {
      return `
          ${props.theme.textStyle(breakpoint, "caption")};
          @media (orientation: landscape) {
            width: ${(
              props.theme.space(breakpoint, 6) * 5 +
              props.theme.space(breakpoint, 8) * 2
            ).toFixed(2)}px;
            
            & span {
              display: block;
            }
          }

          @media (orientation: portrait) {
            justify-content: flex-end;
            max-width: ${props.theme.bodyCopyMaxWidth[breakpoint].toFixed(2)}px;
            width: 100%;
            padding-bottom: ${(
              props.theme.space(breakpoint, 5) +
              props.theme.space(breakpoint, 7)
            ).toFixed(0)}px;
          } 
        `;
    })}

  ${(props) =>
    props.theme.apply(
      ["tablet", "tabletLandscape", "desktop", "screen"],
      (breakpoint: string) => {
        return `
          ${props.theme.textStyle(breakpoint, "caption")};
          @media (orientation: landscape) {
            max-width: ${props.theme.bodyCopyMaxWidth[breakpoint].toFixed(2)}px;
            width: 100%;
            & span {
              display: block;
            }
          }

          @media (orientation: portrait) {
            justify-content: flex-end;
            max-width: ${props.theme.bodyCopyMaxWidth[breakpoint].toFixed(2)}px;
            
            width: calc(100% - ${(
              props.theme.space(breakpoint, 6) * 3 +
              props.theme.space(breakpoint, 7) * 2
            ).toFixed(0)}px);   
          }        
        `;
      }
    )}
`;

const ExhibitionDetail = ({ data }: { data: any }) => {
  const isMounted = useIsMounted();
  const config = useConfigContext();
  const headerContext = useHeaderContext();
  const menuContext = useMenuContext();
  const cssVars = useCssVarsContext();

  const currentScrollbarWidthRef = useRef<number>(0);
  const isFullscreenRef = useRef<boolean>(false);
  const overlayFullyHiddenRef = useRef<boolean>(false);
  const overlayRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const slideshowRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const overlayContentRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const buttonPrevRef = useRef() as React.MutableRefObject<HTMLButtonElement>;
  const buttonNextRef = useRef() as React.MutableRefObject<HTMLButtonElement>;

  const observerAnimationFrame = useRef<boolean>(false);
  const animationFrameRef = useRef<ReturnType<
    typeof requestAnimationFrame
  > | null>(null);

  const exhibitionContentRef =
    useRef() as React.MutableRefObject<HTMLDivElement>;
  const exhibitionContentShadowRef =
    useRef() as React.MutableRefObject<HTMLDivElement>;

  const isContentFixedRef = useRef(true);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  const onFullscreenEnter = () => {
    isFullscreenRef.current = true;
    setIsFullscreen(true);
    headerContext.setObserveScroll(false);
    headerContext.setFadeOut(true);

    currentScrollbarWidthRef.current =
      window.innerWidth - document.body.offsetWidth;

    overlayRef.current.style.height = `${window.innerHeight}px`;

    document.documentElement.style.setProperty(
      "--sbw",
      `${currentScrollbarWidthRef.current}px`
    );

    window.scroll(0, 0);

    document.body.style.overflow = "hidden";
  };

  const onFullscreenExit = () => {
    isFullscreenRef.current = false;
    setIsFullscreen(false);

    document.body.style.overflow = "";
    overlayRef.current.style.height = `${window.innerHeight}px`;

    setTimeout(() => {
      overlayRef.current.style.height = `${window.innerHeight}px`;
      window.dispatchEvent(new CustomEvent("scroll"));
    }, 150);

    headerContext.setFadeOut(false);

    setTimeout(() => {
      headerContext.setObserveScroll(true);
    }, 500);
  };

  const toggleFullscreen = async () => {
    if (!isIOS) {
      if (isFullscreen) {
        try {
          if (typeof screen?.orientation?.unlock === "function")
            screen?.orientation?.unlock();
        } finally {
          screenfull.exit();
        }
      } else {
        onFullscreenEnter();
        try {
          if (typeof screen?.orientation?.lock === "function") {
            await screen?.orientation?.lock("landscape");
          }
        } catch (err) {}
        screenfull.request(slideshowRef.current);
      }
    } else {
      if (!isFullscreen) {
        onFullscreenEnter();

        if (currentScrollbarWidthRef.current > 0) {
          document.body.style.borderRight = `var(--sbw) solid #fff`;
        }
      } else {
        onFullscreenExit();

        if (currentScrollbarWidthRef.current > 0) {
          document.body.style.borderRight = "";
        }

        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("resize"));
        }, 300);
      }

      setIsFullscreen(!isFullscreen);
    }
  };

  const onAnimationFrame = useCallback(() => {
    if (!isMounted) return;
    if (window.scrollY <= window.innerHeight) {
      if (overlayFullyHiddenRef.current) {
        overlayFullyHiddenRef.current = false;
      }

      const cV = cssVars.getVars();

      const maxScrollTop = Math.max(
        0,
        (cV.h100p === cV.h100vh ? window.innerHeight : cV.h100vh) -
          Math.max(0, overlayContentRef.current.offsetTop) +
          2
      );

      const height =
        cV.h100p === cV.h100vh
          ? window.innerHeight - window.scrollY
          : Math.min(cV.h100p, cV.h100vh - window.scrollY);

      overlayRef.current.style.height = `${height}px`;

      if (window.scrollY <= maxScrollTop) {
        if (!isContentFixedRef.current) {
          isContentFixedRef.current = true;
          exhibitionContentRef.current.style.position = `fixed`;
          exhibitionContentRef.current.style.top = `0px`;
        }
      } else {
        if (isContentFixedRef.current) {
          isContentFixedRef.current = false;
          exhibitionContentRef.current.style.position = `absolute`;
          exhibitionContentRef.current.style.top = `${maxScrollTop - 2}px`;
        }
      }
    } else {
      if (!overlayFullyHiddenRef.current) {
        const maxScrollTop = Math.max(
          0,
          (cssVars.getVars().h100p === cssVars.getVars().h100vh
            ? window.innerHeight
            : cssVars.getVars().h100vh) -
            Math.max(0, overlayContentRef.current.offsetTop)
        );
        overlayRef.current.style.height = `0px`;
        overlayFullyHiddenRef.current = true;
        exhibitionContentRef.current.style.position = `absolute`;
        exhibitionContentRef.current.style.top = `${maxScrollTop}px`;
      }
    }

    if (observerAnimationFrame.current) {
      animationFrameRef.current = requestAnimationFrame(onAnimationFrame);
    }
  }, [cssVars, isMounted]);

  const onScroll = useCallback(() => {
    if (window.scrollY <= window.innerHeight * 1.5) {
      if (!observerAnimationFrame.current) {
        observerAnimationFrame.current = true;
        animationFrameRef.current = requestAnimationFrame(onAnimationFrame);
      }
    } else {
      if (observerAnimationFrame.current) {
        observerAnimationFrame.current = false;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }
  }, [onAnimationFrame]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onResize = () => {
      if (exhibitionContentShadowRef.current)
        exhibitionContentShadowRef.current.style.height = `${
          exhibitionContentRef.current.offsetHeight +
          window.innerHeight -
          overlayContentRef.current.offsetTop
        }px`;

      onScroll();
    };
    const onResizeDebounced = debounce(onResize, 100);

    let isIOSDevice = false;

    const screenfullCallbackChange = (event: any) => {
      if (!isMounted) return;

      setIsFullscreen((screenfull as any)?.isFullscreen);

      if (!(screenfull as any)?.isFullscreen && isFullscreenRef.current) {
        onFullscreenExit();
      }
    };
    const screenfullCallbackError = (event: any) => {
      if (!isMounted) return;
      setIsFullscreen(false);
      isFullscreenRef.current = false;
    };

    if (
      (isSafari && primaryInput === "touch") ||
      typeof screenfull?.on !== "function"
    ) {
      setIsIOS(true);
      isIOSDevice = true;
    } else {
      screenfull.on("change", screenfullCallbackChange);
      screenfull.on("error", screenfullCallbackError);
    }

    window.addEventListener("resize", onResizeDebounced);
    window.addEventListener("orientationchange", onResizeDebounced);
    window.addEventListener("change", onResizeDebounced);
    window.addEventListener("scroll", onScroll, { passive: true });

    onResize();

    setTimeout(onResize, 100);

    const triggerOnResize = () => {
      onResize();
    };
    document.addEventListener("DOMContentLoaded", triggerOnResize);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      window.removeEventListener("resize", onResizeDebounced);
      window.removeEventListener("orientationchange", onResizeDebounced);
      window.removeEventListener("change", onResizeDebounced);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("DOMContentLoaded", triggerOnResize);

      if (!isIOSDevice) {
        screenfull.off("change", screenfullCallbackChange);
        screenfull.off("error", screenfullCallbackError);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formattedDateBegin = formatDate(data?.acf?.date_begin);
  const formattedDateEnd = formatDate(data?.acf?.date_end);

  let phase = null;
  try {
    let dateBegin = null;
    let dateEnd = null;
    if (
      typeof data?.acf?.date_begin === "string" &&
      data?.acf?.date_begin.trim()
    )
      dateBegin = new Date(data?.acf?.date_begin);

    if (typeof data?.acf?.date_end === "string" && data?.acf?.date_end.trim())
      dateEnd = new Date(data?.acf?.date_end);

    if (dateBegin) {
      dateBegin = new Date(dateBegin.setHours(0, 0, 0, 0));
      const now = new Date(new Date().setHours(0, 0, 0, 0));

      if (now < dateBegin) {
        phase = "UPCOMING";
      } else {
        if (dateEnd) {
          dateEnd = new Date(dateEnd.setHours(23, 59, 59, 0));
          if (dateBegin <= now && now <= dateEnd) {
            phase = "ON SHOW";

            const tPlus7 = new Date();
            tPlus7.setDate(tPlus7.getDate() + 7);

            if (dateEnd < tPlus7) {
              phase = "CLOSING SOON";
            }
          }
        }
      }

      if (dateEnd && now > dateEnd) {
        phase = "PAST EXHIBITION";
      }
    }
  } catch (e) {}

  const exhibitionText = (
    <>
      Exhibition
      {(formattedDateBegin || formattedDateEnd) && (
        <>
          &emsp;
          <span style={{ fontWeight: 400 }}>
            {formattedDateBegin}
            {formattedDateBegin && formattedDateEnd && <>&ndash;</>}
            {formattedDateEnd}
          </span>
        </>
      )}
    </>
  );

  const exhibitionCaption = (
    <Heading heading="h4" space={9} inline>
      {exhibitionText}
    </Heading>
  );

  const isOffsite =
    data?.taxonomies?.programme_category?.length > 0 &&
    data?.taxonomies?.programme_category?.reduce((flag: boolean, cat: any) => {
      if (cat.slug === "off-site") return true;
      return flag;
    }, false);

  const isInternational =
    data?.taxonomies?.programme_category?.length > 0 &&
    data?.taxonomies?.programme_category?.reduce((flag: boolean, cat: any) => {
      if (cat.slug === "international") return true;
      return flag;
    }, false);
  const info = (
    <Info>
      <div style={{ gridArea: "text" }}>{exhibitionCaption}</div>
      <Badges>
        {isOffsite && (
          <Badge space={8} heading="h4" color="#fff" backgroundColor="#000">
            OFF-SITE
          </Badge>
        )}
        {isInternational && (
          <Badge space={8} heading="h4" color="#fff" backgroundColor="#000">
            INTERNATIONAL
          </Badge>
        )}
        {phase && (
          <Badge space={8} heading="h4" color="#fff" backgroundColor="#000">
            {phase}
          </Badge>
        )}
        <BookTicket space={8} heading="h4" data={data} />
      </Badges>
    </Info>
  );

  return (
    <>
      <NextHeadSeo
        canonical={data?.yoast_head_json?.canonical}
        title={`${
          data?.acf?.artist_name && data?.acf?.artist_name.trim()
            ? `${data?.acf?.artist_name}: `
            : ``
        }${data?.title} - Exhibition - ${config.appTitle}`}
        description={data?.yoast_head_json?.description}
        og={{
          type: data?.yoast_head_json?.og_type,
          siteName: data?.yoast_head_json?.og_site_name,
          image: data?.yoast_head_json?.twitter_image,
        }}
        twitter={{
          card: "summary_large_image",
        }}
      />
      {data.slug === "scroll-down-and-keep-scrolling" && (
        <FonaBannerGlobalStyle />
      )}
      <Header hasHero>
        <SubMenuTwoCol alignSelf="center" colGapSpace={8}>
          <SubMenuTwoCol
            alignSelf="center"
            colGapSpace={8}
            gridTemplateColumns="auto 1fr"
          >
            <Heading
              space={7}
              heading="h3"
              inline
              trimmed
              html={`${
                data?.acf?.artist_name && data?.acf?.artist_name.trim()
                  ? `${data?.acf?.artist_name}: `
                  : ``
              }${data?.title}`}
            />

            <DisplayAbove breakpoint="tabletLandscape" inline>
              <Heading space={0} heading="h4" inline noWrap>
                {exhibitionText}
              </Heading>
            </DisplayAbove>
          </SubMenuTwoCol>
          <Heading space={7} heading="h3" inline>
            <Badges>
              {isOffsite && (
                <Badge
                  space={8}
                  heading="h4"
                  color="#fff"
                  backgroundColor="#000"
                >
                  OFF-SITE
                </Badge>
              )}
              {isInternational && (
                <Badge
                  space={8}
                  heading="h4"
                  color="#fff"
                  backgroundColor="#000"
                >
                  INTERNATIONAL
                </Badge>
              )}
              {phase && (
                <Badge
                  space={8}
                  heading="h4"
                  color="#fff"
                  backgroundColor="#000"
                >
                  {phase}
                </Badge>
              )}
              <BookTicket space={8} heading="h4" data={data} />
            </Badges>
          </Heading>
        </SubMenuTwoCol>
      </Header>
      <Header isOverlay />
      <div id="content" />
      <SlideShow
        isMenuOpen={menuContext.isOpen}
        isFullscreen={isFullscreen}
        isIOS={isIOS}
      >
        {data.acf.hero_images?.length > 0 && (
          <ExhibitionSlideShow
            key={`exhibition-slideshow-${data?.id}`}
            buttonPrevRef={buttonPrevRef}
            buttonNextRef={buttonNextRef}
            isFullscreen={isFullscreen}
            slidesVirtualMin={SLIDES_VIRTUAL_MIN}
            slides={data.acf.hero_images.map((img: any, index: number) => (
              <ExhibitionSlide
                key={`slideshowimg-${index}`}
                isFullscreen={isFullscreen}
              >
                <div style={{ width: "100%", height: "100%" }}>
                  <ApiImage
                    alt={img?.alt}
                    sizes={img?.sizes}
                    imgSizes={!isFullscreen ? themeImgSizes(imgSizes) : "100vw"}
                    aspectRatioDirection="width"
                    mode="fill"
                    objectFit={!isFullscreen ? "cover" : "contain"}
                    objectPosition={
                      !isFullscreen ? img?.cropPosition : "top left"
                    }
                    bgColor={!isFullscreen ? "#000" : "#fff"}
                    loading={index < 3 ? "eager" : "lazy"}
                  />
                </div>
                {isFullscreen && (
                  <Caption>
                    <span>
                      Image {index + 1} of {data.acf.hero_images?.length}&emsp;
                    </span>
                    {img?.caption && (
                      <SafeHtmlSpan html={`<b>${img?.caption}</b>&emsp;`} />
                    )}
                    {img?.description && (
                      <SafeHtmlSpan
                        html={
                          !img?.caption
                            ? `<b>${img?.description}</b>`
                            : img?.description
                        }
                      />
                    )}
                  </Caption>
                )}
              </ExhibitionSlide>
            ))}
          />
        )}
      </SlideShow>
      <Blur isMenuOpen={menuContext.isOpen} isFullscreen={isFullscreen} />
      <Overlay
        ref={overlayRef}
        isFullscreen={isFullscreen}
        isMenuOpen={menuContext.isOpen}
      >
        <OverlayContent>
          <PageMargins>
            <div ref={overlayContentRef}>
              <TitleGrid isFullscreen={isFullscreen}>
                <Title>
                  {info}
                  <Heading
                    space={8}
                    heading="h1"
                    asTag
                    html={data?.acf?.artist_name}
                  />
                  <Heading space={6} heading="h2" asTag html={data?.title} />
                </Title>

                <div></div>
              </TitleGrid>
            </div>
          </PageMargins>
          <SlideshowButtonContainer isFullscreen={isFullscreen} isIOS={isIOS}>
            <SlideshowButton
              rotateButton
              className="prev"
              ref={buttonPrevRef}
              arrow
              aria-label="show previous slide"
              isFullscreen={isFullscreen}
            >
              <SvgBackground width="100%" height="100%" type="arrow" />
            </SlideshowButton>
            <SlideshowButton
              className="next"
              ref={buttonNextRef}
              arrow
              aria-label="show next slide"
              isFullscreen={isFullscreen}
            >
              <SvgBackground width="100%" height="100%" type="arrow" />
            </SlideshowButton>
            <SlideshowButton
              onClick={toggleFullscreen}
              aria-label={
                isFullscreen ? "Leave fullscreen" : "Enter fullscreen mode"
              }
              isFullscreen={isFullscreen}
            >
              <SvgBackground
                width="100%"
                height="100%"
                type="custom"
                svg={isFullscreen ? SvgFullscreenExit : SvgFullscreen}
              />
            </SlideshowButton>
          </SlideshowButtonContainer>
        </OverlayContent>
      </Overlay>
      <ExhibitionContent
        ref={exhibitionContentRef}
        isMenuOpen={menuContext.isOpen}
      >
        <ExhibitionHeader>
          <PageMargins>
            <TitleGrid isFullscreen={false}>
              <Title>
                {info}
                <Heading
                  space={8}
                  heading="h1"
                  asTag
                  html={data?.acf?.artist_name}
                />
                <Heading space={6} heading="h2" asTag html={data?.title} />
              </Title>
              <div />
            </TitleGrid>
          </PageMargins>
        </ExhibitionHeader>

        <PageMargins>
          <ContentWithSidebar>
            {data?.acf?.content?.length > 0 ? (
              <FlexibleContent
                columnWidth="full"
                field="content"
                content={data.acf.content}
              />
            ) : (
              <div />
            )}

            <Sidebar>
              <BookTicket
                space={7}
                heading="h3"
                data={data}
                fullWidth
                WrapWith={SidebarBuyButtonWrapper}
              />

              {(data?.acf?.date_further_information ||
                (data?.acf?.free_event === false &&
                  data?.acf?.ticket_cost)) && (
                <SideBarBlock>
                  <Heading heading="h4" asTag space={8}>
                    Exhibition Details
                  </Heading>
                  <SafeHtmlDiv
                    html={`
                    ${data?.acf?.date_further_information ?? ""}
                    ${
                      data?.acf?.free_event === false && data?.acf?.ticket_cost
                        ? `<p>${data?.acf?.ticket_cost}</p>`
                        : ""
                    }
                    
                    `}
                  />
                </SideBarBlock>
              )}

              {data?.acf?.venue &&
                data?.acf?.venue?.name &&
                data?.acf?.venue?.address && (
                  <SideBarBlock>
                    <Heading heading="h4" asTag space={8}>
                      VENUE
                    </Heading>
                    <SafeHtmlDiv
                      html={`
                      <p><b>${data?.acf?.venue?.name.trim()}</b></p>
                      
                    `}
                    />
                    <SafeHtmlDiv html={data?.acf?.venue?.address} />

                    {data?.acf?.venue?.map?.lat && data?.acf?.venue?.map?.lng && (
                      <MapDiv>
                        <Map
                          lat={data?.acf?.venue?.map?.lat}
                          lng={data?.acf?.venue?.map?.lng}
                          zoom={14}
                          ratio="4 / 3"
                        />
                      </MapDiv>
                    )}
                  </SideBarBlock>
                )}
              <SidebarShare
                title={data?.title}
                url={`${config.baseUrl}/exhibition/${data?.slug}`}
              />
            </Sidebar>
          </ContentWithSidebar>
        </PageMargins>

        {data?.acf?.matterport_embed && (
          <PageMargins>
            <FlexibleContent
              columnWidth="wide"
              field="content-matterport"
              content={[
                {
                  acf_fc_layout: "embed_code",
                  heading: "3D Visit",
                  force16by9: true,
                  embed_code: parsedMatterportEmbed(
                    data?.acf?.matterport_embed
                  ),
                },
              ]}
            />
          </PageMargins>
        )}

        {data?.acf?.media?.length > 0 && (
          <VerticalSlider
            key={`media`}
            header="Media"
            hasNav
            spaceHeight={{
              base: "auto",
              mobile: "auto",
              tablet: "auto",
              desktop: "auto",
              screen: "auto",
            }}
          >
            {data?.acf?.media?.map((item: any, index: number) => (
              <ListItemVideoOrAudio
                item={item}
                key={`media-${index + 1}`}
                inVerticalSlider
              />
            ))}
          </VerticalSlider>
        )}

        {data?.acf?.documents?.length > 0 && (
          <Documents documents={data?.acf?.documents} />
        )}
        <RelatedProducts
          key={`exhibition-products-${data?.id}`}
          id={data?.id}
          ids={data?.acf?.shop_items ?? []}
        />
        <RelatedContent
          key={`exhibition-related-${data?.id}`}
          ids={[data?.id]}
          excludeIds={
            data?.acf?.media?.length > 0
              ? data?.acf?.media?.map((item: any) => item.id)
              : undefined
          }
          relatedItems={data?.acf?.related_content ?? []}
        />
        <Footer />
      </ExhibitionContent>
      <ExhibitionContentShadow
        ref={exhibitionContentShadowRef}
      ></ExhibitionContentShadow>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const events = await restApiESQuery({
    type: "exhibition",
    perPage: 100,
    orderby: "post_date",
    order: "desc",
  });

  return {
    paths: events?.data?.length
      ? events?.data.map((item: any) => item.url)
      : [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const token = (context?.previewData as any)?.token;

  const data = await restApiGetPostBySlugOrFallbackId(
    "exhibition",
    context?.params?.slug,
    token
  );

  const frontendSettings = await restApiESGetSettings(["programmeCategory"]) 
  if (!data)
    return {
      props: {
        frontendSettings,
      },
      notFound: true,
      revalidate: frontendSettings?.options?.copyrightNotice ? 240 : 15,
    };

  return {
    props: {
      frontendSettings,
      data,
    },
    revalidate: frontendSettings?.options?.copyrightNotice
      ? appConfig.revalidateInterval("exhibitionDetail", {
          date: data?.date,
          modified: data?.modified,
        })
      : 15,
  };
};

ExhibitionDetail.getLayout = function getLayout(
  page: ReactElement,
  props: any
) {
  return <LayoutIntroSlideshow props={props}>{page}</LayoutIntroSlideshow>;
};
export default ExhibitionDetail;
